import { PROTOCOL_VERSION, type ClientMessage, type HeroId, type ServerMessage, type WorldSnapshot } from '../../shared/network/protocol.js';
import type { PingMark } from '../../shared/network/pings.js';

export type NetworkEvents = {
  status: (status: string, connected: boolean) => void;
  welcome: (heroIds: HeroId[], room: string, content: import('../../shared/game/world-types.js').GameContent) => void;
  party: (options: Extract<ServerMessage, { type: 'party-options' }>) => void;
  /** Seats another player filled with their characters after this client entered. */
  heroes: (heroes: Extract<ServerMessage, { type: 'heroes' }>['heroes']) => void;
  snapshot: (snapshot: WorldSnapshot) => void;
  error: (message: string) => void;
  latency: (ms: number) => void;
  /** A player in the room pointed something out (this player included). */
  ping: (ping: PingMark) => void;
};
export class GameConnection {
  private socket: WebSocket | null = null;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private ping: ReturnType<typeof setInterval> | null = null;
  private stopped = true;
  private attempts = 0;
  private generation = 0;
  private joined = false;
  private choosing = false;
  constructor(private getToken: () => Promise<string>, private events: NetworkEvents, private serverUrl: string) {}
  connect(room: string, sessionKey?: string) {
    this.disconnect(); this.stopped = false; this.attempts = 0;
    void this.open(room, sessionKey, ++this.generation);
  }
  private async open(room: string, sessionKey: string | undefined, generation: number) {
    this.events.status(this.attempts ? 'Reconnecting…' : 'Connecting…', false);
    let token: string;
    try { token = await this.getToken(); }
    catch { this.events.error('Could not refresh your sign-in. Please sign in again.'); this.disconnect(); return; }
    if (this.stopped || generation !== this.generation) return;
    const endpoint = new URL('/ws', this.serverUrl); endpoint.protocol = endpoint.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(endpoint);
    this.socket = socket;
    socket.onopen = () => socket.send(JSON.stringify({ type: 'join', version: PROTOCOL_VERSION, token, room, sessionKey }));
    socket.onmessage = event => {
      if (this.socket !== socket) return;
      const message = JSON.parse(event.data) as ServerMessage;
      if (message.type === 'party-options') {
        this.choosing = true;
        if (message.selectedHeroIds.length) this.enter({ heroIds: message.selectedHeroIds });
        else { this.events.status('Choose your characters', false); this.events.party(message); }
      } else if (message.type === 'welcome') {
        this.joined = true; this.choosing = false; this.attempts = 0;
        this.events.welcome(message.heroIds, message.room, message.content); this.events.status('Connected', true);
        this.ping = setInterval(() => this.send({ type: 'ping', sentAt: performance.now() }), 2000);
      } else if (message.type === 'snapshot') this.events.snapshot(message);
      else if (message.type === 'heroes') this.events.heroes(message.heroes);
      else if (message.type === 'error') this.events.error(message.message);
      else if (message.type === 'map-ping') { const { type: _, ...ping } = message; this.events.ping(ping); }
      else if (message.type === 'pong') this.events.latency(Math.round(performance.now() - message.sentAt));
    };
    socket.onerror = () => { /* The close callback handles retry and status. */ };
    socket.onclose = event => {
      if (this.socket !== socket) return;
      if (!this.stopped) console.warn('Game connection closed', { code: event.code, reason: event.reason });
      this.joined = this.choosing = false;
      if (this.ping) clearInterval(this.ping); this.ping = null;
      this.events.status('Disconnected', false);
      if (this.stopped || generation !== this.generation) return;
      if (event.code === 4004 || event.code === 4008) { this.stopped = true; return; }
      const delay = Math.min(1000 * 2 ** this.attempts++, 10_000);
      this.retry = setTimeout(() => void this.open(room, sessionKey, generation), delay);
    };
  }
  send(message: ClientMessage): boolean {
    if (!this.joined || this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(message)); return true;
  }
  /** Resume a reserved party by its heroes, or bring saved characters as a new one. */
  enter(choice: { heroIds: HeroId[] } | { characters: unknown[] }): boolean {
    if (!this.choosing || this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify({ type: 'enter', ...choice } satisfies ClientMessage)); return true;
  }
  disconnect() {
    this.stopped = true; this.joined = this.choosing = false; this.generation++;
    if (this.retry) clearTimeout(this.retry);
    if (this.ping) clearInterval(this.ping);
    this.retry = this.ping = null;
    const socket = this.socket; this.socket = null; socket?.close(1000, 'Leaving room');
  }
}
